import { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import UserContext from "common/Context/UserContext";
import CustomerAccessRevoked from "common/Components/CustomerAccessRevoked";
import {
  applyNavigationAfterEffectiveAccessChange,
  resolveNavigationAfterEffectiveAccessChange,
} from "common/Utilities/EffectiveAccess.utils";

const EffectiveAccessRouteGuard = ({ children }) => {
  const { userProfile } = useContext(UserContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const profile = userProfile?.profile ?? {};
  const [blockedMessage, setBlockedMessage] = useState(null);
  const lastAccessSignatureRef = useRef(null);

  useEffect(() => {
    if (!profile?.sessionFromMe || !profile?.effectiveAccess) {
      setBlockedMessage(null);
      lastAccessSignatureRef.current = null;
      return;
    }

    const accessSignature = JSON.stringify({
      permissions: profile.effectiveAccess?.permissions ?? [],
      customerId: profile.activeCustomerId ?? profile.customerId ?? null,
      path: pathname,
    });

    if (lastAccessSignatureRef.current === accessSignature) {
      return;
    }
    lastAccessSignatureRef.current = accessSignature;

    const resolution = resolveNavigationAfterEffectiveAccessChange(
      profile,
      pathname
    );

    if (resolution.action === "stay") {
      setBlockedMessage(null);
      return;
    }

    if (resolution.action === "redirect") {
      setBlockedMessage(null);
      applyNavigationAfterEffectiveAccessChange(profile, pathname, navigate);
      return;
    }

    if (resolution.action === "no_access") {
      setBlockedMessage(resolution.message);
    }
  }, [
    navigate,
    pathname,
    profile,
    profile?.activeCustomerId,
    profile?.customerId,
    profile?.effectiveAccess,
    profile?.sessionFromMe,
  ]);

  if (blockedMessage) {
    return (
      <CustomerAccessRevoked
        title="Access unavailable"
        message={blockedMessage}
        hint=""
      />
    );
  }

  return children;
};

EffectiveAccessRouteGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default EffectiveAccessRouteGuard;
